import { useState, useMemo, useCallback } from 'react';
import { SelectedItem, TabletopItemHandler, TabletopItemType, DeckOperation } from '../types';

interface UseTabletopItemsProps {
  setZoomedCard: React.Dispatch<React.SetStateAction<string | null>>;
  handleCardFlip: (id: string) => void;
  handleDeckOperation: (id: string, operation: DeckOperation) => void;
  onRollDice: (diceId: string) => void;
  handleCardRotate?: (id: string, rotation: number) => void;
  moveCardToHand: (cardId: string) => void;
  removeTableItem: (itemType: 'card' | 'deck' | 'dice' | 'token' | 'coin', id: string) => void;
  rotateDeck: (deckId: string, rotation: number) => void;
  onEditToken?: (id: string) => void;
  onFlipCoin?: (id: string) => void;
}

export const useTabletopItems = ({
  setZoomedCard,
  handleCardFlip,
  handleDeckOperation,
  onRollDice,
  handleCardRotate,
  moveCardToHand,
  removeTableItem,
  rotateDeck,
  onEditToken,
  onFlipCoin
}: UseTabletopItemsProps) => {
  // Unified selection state
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  
  // Handler for card selection
  const handleCardSelect = useCallback((id: string) => {
    setSelectedItem({ id, type: 'card' });
  }, []);
  
  // Handler for deck selection
  const handleDeckSelect = useCallback((id: string) => {
    setSelectedItem({ id, type: 'deck' });
  }, []);
  
  // Generic selection handler
  const handleItemSelect = useCallback((id: string, type: TabletopItemType) => {
    setSelectedItem({ id, type });
  }, []);
  
  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedItem(null);
  }, []);
  
  // Helper to check if an item is selected
  const isItemSelected = useCallback((id: string, type: TabletopItemType) => {
    return selectedItem?.id === id && selectedItem?.type === type;
  }, [selectedItem]);
  
  // Registry of item handlers
  const itemHandlers = useMemo<Record<TabletopItemType, TabletopItemHandler>>(() => ({
    // Card handler
    card: {
      type: 'card',
      getShortcuts: () => [
        { key: 'delete', description: 'Delete card' },
        { key: 'f', description: 'Flip card' },
        { key: 'r', description: 'Rotate 90° clockwise' },
        { key: 'q', description: 'Rotate 90° counter-clockwise' },
        { key: 'z', description: 'Zoom card' },
        { key: 'h', description: 'Move to hand' },
        { key: 'shift + click', description: 'Select all touching cards' },
        { key: 's', description: 'Compact selected cards into a stack' },
        { key: 'option + arrows', description: 'Fan selected stack left, right, up, or down' },
        { key: 'option + drag', description: 'Drop selected cards as a tight stack' }
      ],
      handleKeyboardShortcut: (id, key, state) => {
        switch (key) {
          case 'delete':
            removeTableItem('card', id);
            setSelectedItem(null);
            return null;
          case 'f':
            handleCardFlip(id);
            return null;
          case 'r':
            if (handleCardRotate) {
              const newRotation = (state.cards[id].rotation + 90) % 360;
              handleCardRotate(id, newRotation);
              return null;
            }
            return {
              ...state,
              cards: {
                ...state.cards,
                [id]: {
                  ...state.cards[id],
                  rotation: (state.cards[id].rotation + 90) % 360
                }
              }
            };
          case 'q':
            if (handleCardRotate) {
              const newRotation = (state.cards[id].rotation - 90 + 360) % 360;
              handleCardRotate(id, newRotation);
              return null;
          }
          return {
            ...state,
            cards: {
              ...state.cards,
              [id]: {
                ...state.cards[id],
                rotation: (state.cards[id].rotation - 90 + 360) % 360
              }
            }
          };
        case 'z':
          if (state.cards[id]) {
            setZoomedCard(prevId => (prevId === id ? null : id));
          }
          return null;
        case 'h':
          moveCardToHand(id);
          return null;
          default:
            return null;
        }
      },
      handleSelect: (id) => handleCardSelect(id)
    },
    
    // Deck handler
    deck: {
      type: 'deck',
      getShortcuts: () => [
        { key: 'delete', description: 'Delete deck' },
        { key: 'r', description: 'Rotate deck' },
        { key: 'd', description: 'Draw card (D)' },
        { key: 's', description: 'Shuffle deck' },
        { key: 'g', description: 'Search deck' },
        { key: 'm', description: 'Mulligan deck' }
      ],
      handleKeyboardShortcut: (id, key, state) => {
        switch (key) {
          case 'delete':
            removeTableItem('deck', id);
            setSelectedItem(null);
            return null;
          case 'r': {
            const deck = state.decks[id];
            if (deck) {
              rotateDeck(id, (deck.rotation + 90) % 360);
            }
            return null;
          }
          case 's':
            handleDeckOperation(id, { type: 'shuffle' });
            return null;
          case 'm':
            handleDeckOperation(id, { type: 'mulligan' });
            return null;
          default:
            return null;
        }
      },
      handleSelect: (id) => handleDeckSelect(id)
    },
    
    // Dice handler
    dice: {
      type: 'dice',
      getShortcuts: () => [
        { key: 'delete', description: 'Delete dice' },
        { key: 'r', description: 'Roll dice' }
      ],
      handleKeyboardShortcut: (id, key) => {
        switch (key) {
          case 'delete':
            removeTableItem('dice', id);
            setSelectedItem(null);
            return null;
          case 'r':
            if (onRollDice) onRollDice(id);
            return null;
          default:
            return null;
        }
      },
      handleSelect: (id) => {
        setSelectedItem({ id, type: 'dice' });
      }
    },

    // Token handler
    token: {
      type: 'token',
      getShortcuts: () => [
        { key: 'delete', description: 'Delete token' },
        { key: 'e', description: 'Edit token' }
      ],
      handleKeyboardShortcut: (id, key) => {
        switch (key) {
          case 'delete':
            removeTableItem('token', id);
            setSelectedItem(null);
            return null;
          case 'e':
            if (onEditToken) onEditToken(id);
            return null;
          default:
            return null;
        }
      },
      handleSelect: (id) => {
        setSelectedItem({ id, type: 'token' });
      }
    },

    // Coin handler
    coin: {
      type: 'coin',
      getShortcuts: () => [
        { key: 'delete', description: 'Delete coin' },
        { key: 'f', description: 'Flip coin' }
      ],
      handleKeyboardShortcut: (id, key) => {
        switch (key) {
          case 'delete':
            removeTableItem('coin', id);
            setSelectedItem(null);
            return null;
          case 'f':
            onFlipCoin?.(id);
            return null;
          default:
            return null;
        }
      },
      handleSelect: (id) => {
        setSelectedItem({ id, type: 'coin' });
      }
    }
  }), [
    handleCardSelect,
    handleCardFlip,
    handleDeckSelect,
    handleDeckOperation,
    onRollDice,
    setZoomedCard,
    handleCardRotate,
    moveCardToHand,
    removeTableItem,
    rotateDeck,
    setSelectedItem,
    onEditToken,
    onFlipCoin
  ]);
  
  return {
    selectedItem,
    setSelectedItem,
    handleItemSelect,
    clearSelection,
    isItemSelected,
    itemHandlers
  };
}; 
